import { useState, useRef, useEffect } from "react";
import "./MultiSelectPopover.css";

interface MultiSelectPopoverProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  disabled?: boolean;
  searchable?: boolean;
  /**
   * Lands on the trigger button so a sibling `<label htmlFor>` can name it — the same contract
   * `SelectPopover` has. Without it a caller's label points at nothing and the control is
   * announced by its placeholder alone.
   */
  id?: string;
}

export const MultiSelectPopover = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  searchable,
  id,
}: MultiSelectPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filtered = search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (opt: string) => {
    if (value.includes(opt)) {
      onChange(value.filter((v) => v !== opt));
    } else {
      onChange([...value, opt]);
    }
  };

  const label = value.length === 0
    ? placeholder
    : value.length === 1
      ? value[0]
      : `${value.length} selected`;

  return (
    <div ref={ref} className="msp">
      <button
        type="button"
        id={id}
        className={`msp__trigger${open ? " msp__trigger--open" : ""}${value.length === 0 ? " msp__trigger--empty" : ""}${disabled ? " msp__trigger--disabled" : ""}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        <span className="msp__label">{label}</span>
        <svg
          className={`msp__chevron${open ? " msp__chevron--open" : ""}`}
          width="11"
          height="11"
          viewBox="0 0 11 11"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2 3.5l3.5 3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="msp__menu">
          {searchable && (
            <div className="msp__search-wrap">
              <input
                className="msp__search"
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {value.length > 0 && (
            <div
              className="msp__option msp__option--reset"
              onMouseDown={() => { onChange([]); }}
            >
              Clear all
            </div>
          )}
          <div className="msp__options">
            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <label key={opt} className={`msp__option${checked ? " msp__option--selected" : ""}`}>
                  <input
                    type="checkbox"
                    className="msp__checkbox"
                    checked={checked}
                    onChange={() => toggle(opt)}
                  />
                  {opt}
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div className="msp__empty">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
